
const prune = (items) =>
	items
		.filter((item) => item && item.allowed !== false)
		.map((item) => {
			if( !item.children ) return item;

			const children = prune(item.children);

			return children.length ? { ...item, children } : null;
		})
		.filter(Boolean)
		.map(({ allowed, ...item }) => item);

/**
 * Monta a árvore de navegação para o conjunto de permissões informado.
 *
 * @param {string[]} permissions
 * @returns {Array}
 */
export function buildMenuItems(permissions = []) {
	const can = (permission) => permissions.includes(permission);
	const canAny = (prefix) => permissions.some((permission) => permission.startsWith(prefix));

	return prune([
		{
			key  : "/",
			label: "Início",
			icon : "fi fi-rr-home",
			to   : "/",
		},
		{
			key     : "/administrator",
			label   : "Config. de Acesso",
			icon    : "fi fi-rr-lock",
			children: [
				{
					key    : "/administrator/users",
					label  : "Usuários",
					to     : "/administrator/users",
					allowed: can("users.list"),
				},
				{
					key    : "/administrator/roles-and-permissions",
					label  : "Papéis e Permissões",
					to     : "/administrator/roles-and-permissions",
					allowed: can("roles.list"),
				},
				{
					key    : "/administrator/teams",
					label  : "Fluxos de Aprovação",
					to     : "/administrator/teams",
					allowed: can("roles.list"),
				},
				{
					key    : "/administrator/logs",
					label  : "Logs de Alteração",
					to     : "/administrator/logs",
					allowed: can("log.list"),
				},
			],
		},
		{
			key     : "/cadastrogeral",
			label   : "Config. Gerais",
			icon    : "fi fi-rr-settings",
			children: [
				{
					key    : "/list/companies",
					label  : "Empresas",
					to     : "/list/companies",
					allowed: can("empresas.list"),
				},
				{
					key    : "/list/equipments",
					label  : "Equipamentos",
					to     : "/list/equipments",
					allowed: can("equipamentos.list"),
				},
				{
					key    : "/cadastro/overtime",
					label  : "Horas Extras",
					to     : "/cadastro/overtime",
					allowed: can("horas-extras.list"),
				},
				{
					key    : "/cadastro/dsr",
					label  : "DSR",
					to     : "/cadastro/dsr",
					allowed: can("dsr.list"),
				},
				{
					key    : "/cadastro/perimeters",
					label  : "Perímetros",
					to     : "/cadastro/perimeters",
					allowed: can("perimetro.list"),
				},
			],
		},
		{
			key     : "/cadastro",
			label   : "Cadastros",
			icon    : "fi fi-rr-users",
			children: [
				{
					key    : "/cadastro/time",
					label  : "Horários",
					to     : "/cadastro/time",
					allowed: can("horarios.list"),
				},
				{
					key    : "/cadastro/journeys",
					label  : "Jornadas",
					to     : "/cadastro/journeys",
					allowed: can("jornadas.list"),
				},
				{
					key    : "/cadastro/employees",
					label  : "Funcionários",
					to     : "/cadastro/employees",
					allowed: can("funcionarios.list"),
				},
				{
					key    : "/cadastro/departments",
					label  : "Departamentos",
					to     : "/cadastro/departments",
					allowed: can("departamentos.list"),
				},
				{
					key    : "/cadastro/functions",
					label  : "Funções",
					to     : "/cadastro/functions",
					allowed: can("funcoes.list"),
				},
				{
					key    : "/cadastro/holidays",
					label  : "Feriados",
					to     : "/cadastro/holidays",
					allowed: can("feriados.list"),
				},
				{
					key    : "/cadastro/justifications",
					label  : "Justificativas",
					to     : "/cadastro/justifications",
					allowed: can("justificativas.list"),
				},
				{
					key    : "/cadastro/annual-leave",
					label  : "Banco de Horas",
					to     : "/cadastro/annual-leave",
					allowed: can("banco-de-horas.list"),
				},
			],
		},
		{
			key     : "/gestao",
			label   : "Gestão",
			icon    : "fi fi-rr-clipboard-list",
			children: [
				{
					key    : "/gestao/dailypoint",
					label  : "Ponto Diário",
					to     : "/gestao/dailypoint",
					allowed: can("gestao.ponto-diario"),
				},
				{
					key    : "/gestao/timecard",
					label  : "Cartão Ponto",
					to     : "/gestao/timecard",
					allowed: can("gestao.cartao-ponto"),
				},
				{
					key    : "/gestao/signatures",
					label  : "Assinaturas",
					to     : "/gestao/signatures",
					allowed: can("gestao.assinaturas"),
				},
				{
					key     : "/gestao/lancamentos-coletivos",
					label   : "Lançamentos Coletivos",
					children: [
						{
							key    : "/gestao/lancamentos-coletivos/partial-justifications",
							label  : "Justificativa Parcial",
							to     : "/gestao/lancamentos-coletivos/partial-justifications",
							allowed: can("justificativa-parcial.list"),
						},
						{
							key    : "/gestao/lancamentos-coletivos/afastamentos",
							label  : "Afastamentos",
							to     : "/gestao/lancamentos-coletivos/afastamentos",
							allowed: can("afastamentos.list"),
						},
						{
							key    : "/gestao/lancamentos-coletivos/recaulcular-horarios",
							label  : "Recalcular Horários",
							to     : "/gestao/lancamentos-coletivos/recaulcular-horarios",
							allowed: can("lancamentos-coletivos.recalcular-horarios"),
						},
						{
							key    : "/gestao/lancamentos-coletivos/fechamento-periodo-calculo",
							label  : "Fechamento do Período de Cálculo",
							to     : "/gestao/lancamentos-coletivos/fechamento-periodo-calculo",
							allowed: can("fechamento-periodo-calculo.list"),
						},
						{
							key    : "/gestao/lancamentos-coletivos/excecao-de-jornada",
							label  : "Exceção de Horário/HE",
							to     : "/gestao/lancamentos-coletivos/excecao-de-jornada",
							allowed: can("lancamentos-coletivos.excecao-de-jornada"),
						},
						{
							key    : "/gestao/lancamentos-coletivos/manutencao-de-banco-de-horas",
							label  : "Manutenção do Banco de Horas",
							to     : "/gestao/lancamentos-coletivos/manutencao-de-banco-de-horas",
							allowed: canAny("manutencao-banco-horas"),
						},
					],
				},
				{
					key    : "/gestao/import-afd-file",
					label  : "Importar Arquivo AFD",
					to     : "/gestao/import-afd-file",
					allowed: can("gestao.importar-afd"),
				},
				{
					key    : "/gestao/integrations",
					label  : "Integrações",
					to     : "/gestao/integrations",
					allowed: canAny("integracao."),
				},
			],
		},
		{
			key     : "/relatorios",
			label   : "Relatórios",
			icon    : "fi fi-rr-chart-simple",
			children: [
				{
					key    : "/relatorios/frequency",
					label  : "Cartão Ponto",
					to     : "/relatorios/frequency",
					allowed: can("relatorios.cartao-ponto"),
				},
				{
					key    : "/relatorios/totals",
					label  : "Totais",
					to     : "/relatorios/totals",
					allowed: can("relatorios.totais"),
				},
				{
					key    : "/relatorios/removals",
					label  : "Afastamentos",
					to     : "/relatorios/removals",
					allowed: can("relatorios.afastamentos"),
				},
				{
					key    : "/relatorios/tax-files",
					label  : "Arquivos Fiscais",
					to     : "/relatorios/tax-files",
					allowed: can("relatorios.arquivos-fiscais"),
				},
				{
					key    : "/relatorios/employees",
					label  : "Funcionários",
					to     : "/relatorios/employees",
					allowed: can("relatorios.funcionarios"),
				},
				{
					key    : "/relatorios/mirror-point",
					label  : "Espelho Ponto",
					to     : "/relatorios/mirror-point",
					allowed: can("relatorios.espelho-ponto"),
				},
				{
					key    : "/relatorios/hour-bank-statement",
					label  : "Extrato de Banco de Horas",
					to     : "/relatorios/hour-bank-statement",
					allowed: can("relatorios.extrato-banco-horas"),
				},
				{
					key    : "/relatorios/absenteeism",
					label  : "Absenteísmo",
					to     : "/relatorios/absenteeism",
					allowed: can("relatorios.absenteismo"),
				},
				{
					key    : "/relatorios/occurrences",
					label  : "Ocorrências",
					to     : "/relatorios/occurrences",
					allowed: can("relatorios.ocorrencias"),
				},
				{
					key    : "/relatorios/geolocation",
					label  : "Geolocalização",
					to     : "/relatorios/geolocation",
					allowed: can("relatorios.geolocalizacao"),
				},
				{
					key    : "/relatorios/receipts",
					label  : "Comprovantes",
					to     : "/relatorios/receipts",
					allowed: can("relatorios.comprovantes"),
				},
			],
		},
	]);
}

export function findActiveKey(items, pathname) {
	let best = null;

	const walk = (list) => {
		list.forEach((item) => {
			if( item.children )
			{
				walk(item.children);
				return;
			}

			if( !item.to ) return;

			const isExact  = item.to === pathname;
			const isPrefix = item.to !== "/" && pathname.startsWith(`${item.to}/`);

			if( !isExact && !isPrefix ) return;

			if( !best || item.to.length > best.to.length )
			{
				best = item;
			}
		});
	};

	walk(items);

	return best ? best.key : null;
}

export function findOpenKeys(items, activeKey, trail = []) {
	for( const item of items )
	{
		if( item.key === activeKey ) return trail;

		if( item.children )
		{
			const found = findOpenKeys(item.children, activeKey, [...trail, item.key]);

			if( found ) return found;
		}
	}

	return null;
}
